import React, {useEffect, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {ContentHotel} from '@findhotel/sapi'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import useGenerateAppDownloadUrl from 'hooks/useGenerateAppDownloadUrl'
import {getIsOfferSoldOut} from 'modules/accommodation/selectors'
import {getUserCountryCode} from 'modules/meta/selectors'
import {
  getBookOnAppConfirmationOverlay,
  getBookOnAppConfirmationOverlayVisibility
} from 'modules/overlay/selectors'
import {hideOverlay} from 'modules/overlay/slice'
import {BookOnAppConfirmationOverlayInfo} from 'modules/overlay/types'
import {
  getAnchorPriceNightlyPerRoom,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {getAnchorPriceTotal} from 'modules/sapiSearch/utils'
import {
  getAccommodationPageHotelTimezone,
  getIsRoomsPollingComplete,
  getRoomById,
  getRoomOfferById
} from 'modules/searchApi/selectors'

import {navigateTo} from '@vio/core/src/_web/utils/browser/navigateTo'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page,
  Team
} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {getTaxDisplayLogic} from '@vio/core/src/localization/business/countries'
import {getRoomOfferPrices} from '@vio/core/src/price/business/roomOfferPrices'
import {numberOfRooms} from '@vio/core/src/room/business/roomConfiguration'
import {updateAllPrices} from '@vio/core/src/room/business/transformSapiRoomsResponse/decoratePrices'

import {BookOnAppConfirmationSheet} from './BookOnAppConfirmationSheet'

type OverlayOfferCheck = BookOnAppConfirmationOverlayInfo['offerCheck']

const useCheckedStayPrices = ({
  hotelId,
  offerCheck,
  rooms
}: {
  hotelId?: string
  offerCheck?: OverlayOfferCheck
  rooms?: string
}) => {
  const userCountry = useSelector(getUserCountryCode)
  const {offerEntity} = useAccommodationTopOffers({hotelId})
  const anchorNightly = useSelector(state =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )

  return useMemo(() => {
    if (!offerCheck || !rooms) return null
    const {checkIn, checkOut, prices} = offerCheck
    const taxDisplayLogic = getTaxDisplayLogic(userCountry)
    const checkedPrices = prices.map(price =>
      updateAllPrices(price, {checkIn, checkOut, rooms}, taxDisplayLogic)
    )
    return getRoomOfferPrices(checkedPrices, {
      numberOfRooms: numberOfRooms(rooms),
      anchorNightly,
      anchorTotal: getAnchorPriceTotal(offerEntity)
    })
  }, [offerCheck, rooms, userCountry, anchorNightly, offerEntity])
}

/** The overlay is restored from the URL on reload, so it can outlive its offer */
const useIsSheetOpen = ({
  offerId,
  hasOffer
}: {
  offerId?: string
  hasOffer: boolean
}) => {
  const dispatch = useDispatch()
  const isVisible = useSelector(getBookOnAppConfirmationOverlayVisibility)
  const isOfferSoldOut = useSelector(state => getIsOfferSoldOut(state, offerId))
  const isRoomsComplete = useSelector(getIsRoomsPollingComplete)
  const isOfferGone = (!hasOffer && isRoomsComplete) || Boolean(isOfferSoldOut)
  const shouldHide = isVisible && isOfferGone

  useEffect(() => {
    if (shouldHide) dispatch(hideOverlay())
  }, [shouldHide, dispatch])

  return isVisible && !isOfferGone
}

export const BookOnAppConfirmation = ({hotel}: {hotel: ContentHotel}) => {
  const dispatch = useDispatch()
  const {roomId, offerId, placement, clickedOfferRate, currency, offerCheck} =
    useSelector(getBookOnAppConfirmationOverlay) || {}
  const room = useSelector(state => getRoomById(state, roomId))
  const offer = useSelector(state => getRoomOfferById(state, offerId))
  const {rooms} = useSelector(getSearchParameters) || {}
  const hotelTimeZone = useSelector(getAccommodationPageHotelTimezone)
  const {generateAppLockedDealDeepLink} = useGenerateAppDownloadUrl(Page.Search)
  const hotelId = hotel?.objectID
  const stayPrices = useCheckedStayPrices({hotelId, offerCheck, rooms})
  const isOpen = useIsSheetOpen({offerId, hasOffer: Boolean(offer)})

  if (!hotel || !offerCheck || !offer || !rooms || !stayPrices) return null

  const track = (action: Action) =>
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.BookOnAppConfirmation,
        action,
        team: Team.Select,
        page: Page.Accommodation,
        payload: {bannerId: placement}
      })
    )

  const onClose = () => {
    track(Action.Closed)
    dispatch(hideOverlay())
  }

  const onContinue = () => {
    track(Action.Clicked)
    const appDownloadUrl = generateAppLockedDealDeepLink(placement, {
      hotelId,
      clickedOfferRate,
      currency
    })
    // Leaving the page right away drops the Clicked event
    setTimeout(
      () => navigateTo(appDownloadUrl),
      TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY
    )
  }

  return (
    <BookOnAppConfirmationSheet
      isOpen={isOpen}
      hotel={hotel}
      roomName={room?.name ?? ''}
      offer={offer}
      prices={stayPrices}
      paymentMode={offerCheck.paymentMode}
      checkIn={offerCheck.checkIn}
      checkOut={offerCheck.checkOut}
      rooms={rooms}
      hotelTimeZone={hotelTimeZone}
      onClose={onClose}
      onContinue={onContinue}
    />
  )
}
